// Dagre-based vertical (top-to-bottom) layout for the Architecture Output tree.
// React Flow does not auto-layout; this maps Architecture nodes -> {x,y}.
//
// Important: re-run this ONLY when the structure changes (add/remove/move).
// Do not run it on every render (it would thrash positions on pan/zoom).

import dagre from "@dagrejs/dagre";

const STRUCTURAL_SIZE = { width: 280, height: 84 };
const RESOURCE_SIZE = { width: 220, height: 76 };

export const NODE_SIZE = { STRUCTURAL_SIZE, RESOURCE_SIZE };

/**
 * Compute positions for nodes + edges using Dagre (rankdir TB).
 *
 * @param {Array} nodes  ArchNodes
 * @param {Array} edges  Array of {source,target} (structural parent->child)
 * @returns {Map<string,{x:number,y:number,width:number,height:number}>}
 */
export function computeLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: "TB",
    nodesep: 32,
    ranksep: 84,
    marginx: 32,
    marginy: 32,
  });

  for (const n of nodes) {
    const size = n.kind === "structural" ? STRUCTURAL_SIZE : RESOURCE_SIZE;
    g.setNode(n.id, { width: size.width, height: size.height });
  }
  for (const e of edges) {
    if (g.hasNode(e.source) && g.hasNode(e.target)) {
      g.setEdge(e.source, e.target);
    }
  }
  dagre.layout(g);

  const out = new Map();
  for (const n of nodes) {
    const node = g.node(n.id);
    const size = n.kind === "structural" ? STRUCTURAL_SIZE : RESOURCE_SIZE;
    if (!node) continue;
    // Dagre gives the CENTER; React Flow expects the TOP-LEFT.
    out.set(n.id, {
      x: node.x - size.width / 2,
      y: node.y - size.height / 2,
      width: size.width,
      height: size.height,
    });
  }
  return out;
}

/** Build the structural edge list (parent -> child) from the Architecture nodes. */
export function structuralEdges(nodes) {
  const out = [];
  for (const n of nodes) {
    if (n.parentId) {
      out.push({ id: `e-${n.parentId}-${n.id}`, source: n.parentId, target: n.id });
    }
  }
  return out;
}
